/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { saveAs } from 'file-saver';
import { apiGet } from '../../utils/apiClient';
import TableLoader from '../../components/common/TableLoader';
import {
  rowHoverBg,
  selectedRowBg,
  walkInRowBg,
} from '../../utils/rowHighlight';
import { SuccessResponse } from '../../types/CourseStageAccordion.type';
import {
  EmptyValue,
  isParentEmpty,
  isAnonymousUserId,
} from '../../components/common/EmptyValue';
import { useTranslation } from '../../i18n';

const CustomerTable: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [customers, setCustomers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch customers with services
      const customersData = await apiGet<SuccessResponse>(
        `/report/get-customers-services?page=${page + 1}&limit=${rowsPerPage}&search=${searchTerm}`,
      );
      setCustomers(customersData.data);
      setTotalItems(customersData.metadata.totalItems);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleExportCustomers = async () => {
    setExportLoading(true);
    try {
      const query = searchTerm
        ? `?search=${encodeURIComponent(searchTerm)}`
        : '';
      const response = await apiGet<Blob>(
        `/report/export-customer-report${query}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([response as any], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `customer_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t('report.cust_customerList')}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder={t('report.cust_searchPlaceholder')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchData();
                setPage(0);
              }
            }}
            sx={{ width: { xs: '100%', sm: 250 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Tooltip title={t('report.cust_downloadReport')}>
            <IconButton
              onClick={handleExportCustomers}
              loading={exportLoading}
              disabled={totalItems === 0 || exportLoading}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <TableContainer>
        <Table size="small" padding="checkbox">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#4da7db' }}>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_id')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_avatar')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_fullName')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_contact')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_parent')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_userTypeCreatedAt')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.cust_dateOfBirth')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableLoader colSpan={7} />
            ) : (
              <>
            {customers.map((customer) => {
              // Anonymous walk-in customer — muted, matching the Registration table.
              const isAnonymous = isAnonymousUserId(customer.id);
              return (
              <TableRow
                key={customer.id}
                sx={{
                  '&:hover': { backgroundColor: rowHoverBg },
                  backgroundColor:
                    highlightedRow === `data_${customer.id}`
                      ? selectedRowBg
                      : isAnonymous
                        ? walkInRowBg
                        : 'inherit',
                  ...(isAnonymous && {
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }),
                }}
                onClick={() => {
                  if (highlightedRow === `data_${customer.id}`) {
                    setHighlightedRow(null);
                  } else {
                    setHighlightedRow(`data_${customer.id}`);
                  }
                }}
              >
                <TableCell
                  sx={{
                    ...(isAnonymous && { borderLeft: '3px solid #f0ad4e' }),
                  }}
                >
                  {isAnonymous ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{customer.id}</span>
                      <Chip
                        label={t('report.cust_anonymous')}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Stack>
                  ) : (
                    customer.id
                  )}
                </TableCell>
                <TableCell>
                  <Avatar src={customer.pictureUrl} />
                </TableCell>
                <TableCell>
                  {customer.firstName} {customer.lastName} (
                  {customer.preferredName || <EmptyValue />})
                </TableCell>
                <TableCell>
                  <div>{customer.phone || <EmptyValue />}</div>
                  <div>{customer.email || <EmptyValue />}</div>
                </TableCell>
                <TableCell>
                  {isParentEmpty(customer.parent) ? (
                    <EmptyValue />
                  ) : (
                    customer.parent
                  )}
                </TableCell>
                <TableCell>
                  <div>{customer.userType || 'CHILD/ADULT'}</div>
                  <div style={{ fontSize: '0.8em', color: '#666' }}>
                    {new Date(customer.createdAt).toLocaleString()}
                  </div>
                </TableCell>
                <TableCell>
                  {new Date(customer.dob).toLocaleDateString()}
                </TableCell>
              </TableRow>
              );
            })}
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('report.cust_noCustomers')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );
};

export default CustomerTable;
